import React, { useEffect, useState } from "react";
import {Tooltip ,Container, Row, Col, Card, CardBody, Collapse, Button, Progress, Table, Spinner } from "reactstrap";
import { FaMoneyBillWave, FaChartPie, FaBalanceScale, FaPercent, FaCoins } from "react-icons/fa";
import { Info } from "lucide-react";
import { getFinanceDashboardData } from "helpers/backend_helper";
const formatCurrency = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const calculatePercentage = (paid, total) => {
  const p = Number(paid || 0);
  const t = Number(total || 0);
  return t === 0 ? 0 : ((p / t) * 100).toFixed(1);
};

const AcademicFeesDashboard = ({ aYear }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [expandedCenter, setExpandedCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(aYear);

  useEffect(() => {
    if (!selectedYear) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getFinanceDashboardData({ academic_year: selectedYear });
        if (response?.data.success) {
          setDashboardData(response.data.data);
          const firstCenter = response.data.data.centers?.[0];
          if (firstCenter) {
            setExpandedCenter(firstCenter.center_id);
          }
        } else {
          console.error("Failed to fetch dashboard data");
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedYear]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center vh-100">
        <Spinner color="primary" />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="d-flex align-items-center justify-content-center vh-100 text-danger">
        Failed to load dashboard data.
      </div>
    );
  }

  const { overall, centers } = dashboardData;

  // Calculate overall values
 const totalCollected =
        Number(overall.term_paid_amount) +
        Number(overall.otherfees_paid_amount) +
        Number(overall.observation_paid_amount) +
        Number(overall.registeration_paid_amount)+
        Number(overall.term_latefee_paid_amount) +
        Number(overall.otherfees_latefee_paid_amount);

const totalAmount =
        Number(overall.term_total_amount) +
        Number(overall.otherfees_total_amount)+
        Number(overall.observation_paid_amount) +
        Number(overall.registeration_paid_amount)+
        Number(overall.term_latefee_paid_amount) +
        Number(overall.otherfees_latefee_paid_amount)-
        Number(overall.term_discounted_amount);
  const totalBalance =
    Number(overall.term_balance_amount) + Number(overall.otherfees_balance_amount);

  const totalDiscount = Number(overall.term_discounted_amount);

  const totalLateFee =
    Number(overall.term_latefee_paid_amount) +
    Number(overall.otherfees_latefee_paid_amount);

  const totalPaymentChannelAmount = Object.values(overall.payment_channels || {}).reduce(
    (sum, val) => sum + Number(val),
    0
  );

  return (
    <div className="container-fluid px-3">
      <Container fluid className="py-2 min-vh-100">

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-2">
            {/* <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "#4f46e5",
                display: "grid",
                placeItems: "center",
              }}
            >
              <Receipt size={20} color="#fff" />
            </div> */}
            <h4 className="fw-bold mb-0" style={{ color: "#1f2937" }}>
              {/* Fees Dashboard */}
            </h4>
          </div>
          {/* keep your AY selector spot if needed */}
        </div>

        {/* Summary Cards */}
        <Row className="g-3 mb-4 flex-nowrap" style={{ overflowX: "auto" }}>
          <SummaryCard
            title="Total Collected"
            amount={totalCollected}
            icon={<FaMoneyBillWave color="#16a34a" />} // green
            formula="Term + Other + Observation + Registration + Late "

            badgeColor="#dcfce7"
          />
          <SummaryCard
            title="Total Amount"
            amount={totalAmount}
            icon={<FaChartPie color="#0ea5e9" />} // sky
            badgeColor="#e0f2fe"
            formula="Term Total + Other Total + Late + Observation + Registration - Discount"
          />
          <SummaryCard
            title="Balance Due"
            amount={totalBalance}
            icon={<FaBalanceScale color="#dc2626" />} // red
            badgeColor="#fee2e2"
            formula="Term Balance + Other Balance"

          />
         
        </Row>

        {/* Overall Payment Channels */}
        <Card className="mb-4 border-0 shadow-sm rounded-3">
          <CardBody>
            <div className="d-flex align-items-center gap-2 mb-3">
              <div
                style={{
                  width: 8,
                  height: 24,
                  background: "#7c3aed",
                  borderRadius: 4,
                }}
              />
              <h5 className="fw-bold mb-0" style={{ color: "#1f2937" }}>
                Overall Payment Channels
              </h5>
            </div>

            <Row className="g-3">
              {Object.entries(overall.payment_channels || {}).map(([channel, amount], i) => (
                <Col key={i} style={{ flex: 1, minWidth: "180px" }}>
                  <div
                    className="p-3 rounded text-center shadow-sm h-100 border bg-white"
                    style={{ transition: "transform .15s ease" }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                  >
                    <div className="fw-semibold text-primary fs-6">{channel}</div>
                    <div className="fs-5 mt-1 fw-bold text-dark">{formatCurrency(amount)}</div>
                  </div>
                </Col>
              ))}
              <Col style={{ flex: 1, minWidth: "180px" }}>
                <div className="p-3 rounded text-center shadow-sm h-100 border" style={{ background: "#eff6ff" }}>
                  <span className="text-primary fw-semibold">Total</span>
                  <div className="fs-5 mt-1 text-primary fw-bold">
                    {formatCurrency(totalPaymentChannelAmount)}
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Center-wise Fee Collection */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <div style={{ width: 8, height: 24, background: "#f59e0b", borderRadius: 4 }} />
          <h4 className="fw-bold mb-0" style={{ color: "#1f2937" }}>
            Center-wise Fee Collection
          </h4>
        </div>

        {centers.map((center) => {
          const collected =
            Number(center.term_paid_amount) +
            Number(center.otherfees_paid_amount) +
            Number(center.observation_paid_amount) +
            Number(center.registeration_paid_amount);

          const totalAmt =
            Number(center.term_total_amount) + Number(center.otherfees_total_amount);

          const collectionPercent = calculatePercentage(collected, totalAmt);
          const totalChannel = Object.values(center.payment_channels || {}).reduce(
            (sum, val) => sum + Number(val),
            0
          );

          return (
            <Card key={center.center_id} className="mb-4 shadow-sm border-0 rounded-3 overflow-hidden">
              <div
                className="p-3 d-flex justify-content-between align-items-center bg-white"
                style={{ borderBottom: "1px solid #e5e7eb" }}
              >
                <div className="d-flex flex-column">
                  <span className="fw-bold" style={{ color: "#1d4ed8", letterSpacing: ".2px" }}>
                    {(center.center_name || "Unknown Center").toUpperCase()}
                  </span>
                  <small className="text-muted">📍 {center.center_location}</small>
                </div>
                <div className="text-end">
                  <div className="fw-bold text-success fs-5">{formatCurrency(collected)}</div>
                  <div className="d-flex align-items-center gap-2 justify-content-end mt-1">
                    <Progress value={collectionPercent} color="success" style={{ width: "110px", height: "6px" }} />
                    <small className="text-muted fw-semibold">{collectionPercent}%</small>
                  </div>
                </div>
              </div>

              <Collapse isOpen={expandedCenter === center.center_id}>
                <CardBody className="bg-light pt-3">
                  <Row className="g-3">
                    <Col md="6">
                      <FeeBreakdownCard
                        title="Term Fees"
                        paid={center.term_paid_amount}
                        total={center.term_total_amount}
                        discount={center.term_discounted_amount}
                        late={center.term_latefee_paid_amount}
                        balance={center.term_balance_amount}
                      />
                    </Col>
                    <Col md="6">
                      <FeeBreakdownCard
                        title="Other Fees"
                        paid={center.otherfees_paid_amount}
                        total={center.otherfees_total_amount}
                        late={center.otherfees_latefee_paid_amount}
                        discount={0}
                        balance={center.otherfees_balance_amount}
                      />
                    </Col>
                  </Row>

                  {/* Observation & Registration — kept, with light look */}
                  <Row className="g-3 mt-2">
                    <Col md="6">
                      <InfoCard
                        title="Observation Fees"
                        amount={center.observation_paid_amount}
                        color="#6f42c1"
                      />
                    </Col>
                    <Col md="6">
                      <InfoCard
                        title="Registration Fees"
                        amount={center.registeration_paid_amount}
                        color="#20c997"
                      />
                    </Col>
                  </Row>

                  {/* Payment Channels Table */}
                  <div className="d-flex align-items-center gap-2 mt-4 mb-2">
                    <div style={{ width: 6, height: 18, background: "#0ea5e9", borderRadius: 3 }} />
                    <h6 className="fw-semibold mb-0" style={{ color: "#0f172a" }}>
                      Payment Channels
                    </h6>
                  </div>

                  <div className="table-responsive">
                    <Table bordered size="sm" striped className="align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="px-3">Channel</th>
                          <th className="px-3 text-end">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(center.payment_channels || {}).map(([channel, amount], i) => (
                          <tr key={i}>
                            <td className="px-3">{channel}</td>
                            <td className="px-3 text-end">{formatCurrency(amount)}</td>
                          </tr>
                        ))}
                        <tr className="fw-bold" style={{ background: "#eff6ff" }}>
                          <td className="px-3">Total</td>
                          <td className="px-3 text-end text-primary">{formatCurrency(totalChannel)}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Collapse>

              <div className="text-center py-2 bg-white">
                <Button
                  color="link"
                  className="fw-semibold text-primary text-decoration-none"
                  onClick={() =>
                    setExpandedCenter(expandedCenter === center.center_id ? null : center.center_id)
                  }
                >
                  {expandedCenter === center.center_id ? "▲ Hide Details" : "▼ View Details"}
                </Button>
              </div>
            </Card>
          );
        })}
      </Container>
    </div>
  );
};

/* ---------- Components (lightweight, with colored icon badges) ---------- */
const SummaryCard = ({ title, amount, icon, badgeColor = "#eef2ff", formula }) => {
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

  const infoId = `info-${title.replace(/\s+/g, "-")}`;

  return (
    <Col style={{ minWidth: 220 }}>
      <div className="p-3 rounded bg-white border h-100 shadow-sm" style={{ minHeight: 120 }}>
        <div className="d-flex align-items-center justify-content-between mb-2">
          {/* Left Side: Icon + Title */}
          <div className="d-flex align-items-center gap-3">
            <div
              className="rounded"
              style={{
                width: 42,
                height: 42,
                display: "grid",
                placeItems: "center",
                background: badgeColor,
              }}
            >
              <span style={{ fontSize: 20 }}>{icon}</span>
            </div>
            <div className="fw-semibold text-muted">{title}</div>
          </div>

          {/* Right Side: Info Icon */}
          {formula && (
            <>
              <button
                id={infoId}
                className="border-0 bg-transparent p-0 d-flex align-items-center"
                style={{ cursor: "pointer" }}
              >
                <Info size={18} color="#6b7280" /> {/* gray-500 */}
              </button>
              <Tooltip
                placement="top"
                isOpen={tooltipOpen}
                target={infoId}
                toggle={toggleTooltip}
                autohide={true}
              >
                {formula}
              </Tooltip>
            </>
          )}
        </div>

        {/* Amount */}
        <div className="fs-5 fw-bold text-dark">{formatCurrency(amount)}</div>
      </div>
    </Col>
  );
};


const FeeBreakdownCard = ({ title, total, paid, discount = 0, late = 0, balance }) => (
  <div className="p-3 rounded bg-white border shadow-sm h-100">
    <div className="d-flex justify-content-between align-items-center mb-2">
      <h6 className="fw-bold mb-0" style={{ color: "#0ea5e9" }}>{title}</h6>
      <span
        className="badge fw-semibold"
        style={{ background: "#dcfce7", color: "#166534" }}
      >
        {calculatePercentage(paid, total)}% Paid
      </span>
    </div>

    <Progress
      value={calculatePercentage(paid, total)}
      color="success"
      className="mb-3"
      style={{ height: "6px", borderRadius: "4px" }}
    />

    <Table size="sm" borderless responsive className="mb-0">
      <tbody>
        <tr className="border-bottom">
          <td>Total</td>
          <td className="text-end fw-semibold">{formatCurrency(total)}</td>
        </tr>
        <tr className="border-bottom">
          <td>Paid</td>
          <td className="text-end text-success fw-semibold">{formatCurrency(paid)}</td>
        </tr>
        {Number(discount) > 0 && (
          <tr className="border-bottom">
            <td>Discount</td>
            <td className="text-end text-warning">{formatCurrency(discount)}</td>
          </tr>
        )}
        {Number(late) > 0 && (
          <tr className="border-bottom">
            <td>Late Fee</td>
            <td className="text-end text-info">{formatCurrency(late)}</td>
          </tr>
        )}
        <tr>
          <td>Balance</td>
          <td className="text-end text-danger fw-semibold">{formatCurrency(balance)}</td>
        </tr>
      </tbody>
    </Table>
  </div>
);

const InfoCard = ({ title, amount, color }) => (
  <div className="p-3 rounded bg-white border shadow-sm h-100">
    <div className="d-flex justify-content-between align-items-center mb-0">
      <h6 className="fw-bold mb-0" style={{ color }}>{title}</h6>
      <span className="fw-semibold text-success">{formatCurrency(amount)}</span>
    </div>
  </div>
);

export default AcademicFeesDashboard;
